import ElevationTabIcon from '@assets/player/icons/tab_elevation.svg';
import FlowTabIcon from '@assets/player/icons/tab_flow.svg';
import InfoTabIcon from '@assets/player/icons/tab_info.svg';
import LeaderboardTabIcon from '@assets/player/icons/tab_leaderboard.svg';
import StatsTabIcon from '@assets/player/icons/tab_stats.svg';
import classnames from 'classnames';
import { clamp } from 'lodash';
import { memo, useEffect, useState } from 'react';
import { getStarterPoints } from 'src/lib/player/statistics';
import { type EventState, InfoSheetTabs, useStore } from 'src/lib/player/store';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import { trackEvent } from '../../lib/googleAnalytics';
import HeightChart from './HeightChart/HeightChart';
import FlowChart from './InfoSheet/FlowChart';
import InfoSection from './InfoSheet/InfoSection';
import Sheet from './InfoSheet/Sheet';
import StarterInfoTable from './InfoSheet/StarterInfoTable';

const TAB_BAR_HEIGHT = 40;
const CHART_MIN_HEIGHT = 140;
const CHART_MAX_HEIGHT = 500;

export function useAvailableInfoSheetTabs(
  currentEvent: EventState | null,
): Record<InfoSheetTabs, boolean> {
  const hasSelectedItems = useStore((state) => {
    return (
      currentEvent != null &&
      state.timer.selected.some(
        (id) =>
          currentEvent.pois.some((poi) => poi.id === id) ||
          currentEvent.starters.some((starter) => starter.id === id),
      )
    );
  });
  const hasSelectedStartersWithPoints = useStore((state) => {
    return (
      currentEvent != null &&
      state.timer.selected.some((id) => {
        const starter = currentEvent.starters.find((starter) => starter.id === id);
        if (starter != null) {
          return getStarterPoints(state, starter)
            .map((p) => p.length > 0)
            .getOrElse(() => false);
        }
        return false;
      })
    );
  });

  if (currentEvent == null) {
    return {
      INFO: false,
      ELEVATION: false,
      FLOW: false,
      STATS: false,
      TIMING: false,
    };
  }

  const disabledTabs = currentEvent.options.infoTabs.disabledTabs;
  const hasShadowTrack = currentEvent.chunkedShadowTrack != null;
  const hasTiming = currentEvent.modules.timing.enabled;

  const availableTabs: Record<InfoSheetTabs, boolean> = {
    INFO: !disabledTabs.includes(InfoSheetTabs.INFO),
    ELEVATION: hasShadowTrack && !disabledTabs.includes(InfoSheetTabs.ELEVATION),
    FLOW:
      hasShadowTrack && hasSelectedStartersWithPoints && !disabledTabs.includes(InfoSheetTabs.FLOW),
    STATS: hasSelectedItems && !disabledTabs.includes(InfoSheetTabs.STATS),
    TIMING: hasTiming && !disabledTabs.includes(InfoSheetTabs.TIMING),
  };
  return availableTabs;
}

function getFallbackTab(availableTabs: Record<InfoSheetTabs, boolean>): InfoSheetTabs | null {
  if (availableTabs.ELEVATION) return InfoSheetTabs.ELEVATION;
  if (availableTabs.INFO) return InfoSheetTabs.INFO;
  if (availableTabs.STATS) return InfoSheetTabs.STATS;
  if (availableTabs.FLOW) return InfoSheetTabs.FLOW;
  if (availableTabs.TIMING) return InfoSheetTabs.TIMING;
  return null;
}

function InfoSheetContent({ width, height }: { width: number; height: number }) {
  const roundedHeight = Math.floor(height);

  const currentEvent = useStore((state) => state.events[state.currentEventIndices[0]]);
  const defaultTab = currentEvent.options.infoTabs.defaultTab;

  const availableTabs = useAvailableInfoSheetTabs(currentEvent);
  const [activeTab, setActiveTab] = useState<InfoSheetTabs | null>(
    availableTabs[defaultTab] ? defaultTab : getFallbackTab(availableTabs),
  );

  useEffect(
    () => {
      if (activeTab == null || !availableTabs[activeTab]) {
        setActiveTab(availableTabs[defaultTab] ? defaultTab : getFallbackTab(availableTabs));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Object.keys(availableTabs)
      .sort()
      .map((key) => availableTabs[key as InfoSheetTabs]),
  );

  const shouldShowTabBar = Object.values(availableTabs).reduce((r, a) => r + (a ? 1 : 0), 0) > 1;
  const tabBarHeight = shouldShowTabBar ? TAB_BAR_HEIGHT : 0;

  const selectTab = (tab: InfoSheetTabs) => {
    trackEvent('infosheet_tab', { tab });
    setActiveTab(tab);
  };

  return (
    <div className="info-sheet-content">
      {shouldShowTabBar && (
        <div className="info-sheet-tab-bar">
          {availableTabs.INFO && (
            <div
              className={classnames('info-sheet-tab-button', {
                active: activeTab === InfoSheetTabs.INFO,
              })}
              {...onClickGenerator(() => selectTab(InfoSheetTabs.INFO))}
            >
              <img src={InfoTabIcon} alt="Info" />
            </div>
          )}
          {availableTabs.ELEVATION && (
            <div
              className={classnames('info-sheet-tab-button', {
                active: activeTab === InfoSheetTabs.ELEVATION,
              })}
              {...onClickGenerator(() => selectTab(InfoSheetTabs.ELEVATION))}
            >
              <img src={ElevationTabIcon} alt="Elevation" />
            </div>
          )}
          {availableTabs.FLOW && (
            <div
              className={classnames('info-sheet-tab-button', {
                active: activeTab === InfoSheetTabs.FLOW,
              })}
              {...onClickGenerator(() => selectTab(InfoSheetTabs.FLOW))}
            >
              <img src={FlowTabIcon} alt="Race Flow Chart" />
            </div>
          )}
          {availableTabs.STATS && (
            <div
              className={classnames('info-sheet-tab-button', {
                active: activeTab === InfoSheetTabs.STATS,
              })}
              {...onClickGenerator(() => selectTab(InfoSheetTabs.STATS))}
            >
              <img src={StatsTabIcon} alt="Statistics" />
            </div>
          )}
          {availableTabs.TIMING && (
            <div
              className={classnames('info-sheet-tab-button', {
                active: activeTab === InfoSheetTabs.TIMING,
              })}
              {...onClickGenerator(() => selectTab(InfoSheetTabs.TIMING))}
            >
              <img src={LeaderboardTabIcon} alt="Statistics" />
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-around' }}>
        {activeTab === InfoSheetTabs.INFO && availableTabs.INFO && (
          <div
            style={{
              width: Math.floor(width),
              height: roundedHeight - tabBarHeight,
              overflowY: 'auto',
            }}
          >
            <InfoSection />
          </div>
        )}
        {activeTab === InfoSheetTabs.ELEVATION && availableTabs.ELEVATION && (
          <HeightChart
            width={Math.floor(width)}
            height={clamp(roundedHeight - tabBarHeight, CHART_MIN_HEIGHT, CHART_MAX_HEIGHT)}
          />
        )}
        {activeTab === InfoSheetTabs.FLOW && availableTabs.FLOW && (
          <FlowChart
            width={Math.floor(width)}
            height={clamp(roundedHeight - tabBarHeight, CHART_MIN_HEIGHT, CHART_MAX_HEIGHT)}
          />
        )}
        {activeTab === InfoSheetTabs.STATS && availableTabs.STATS && (
          <div
            style={{
              width: Math.floor(width),
              height: roundedHeight - tabBarHeight,
              overflowY: 'auto',
            }}
          >
            <StarterInfoTable />
          </div>
        )}
        {activeTab === InfoSheetTabs.TIMING && availableTabs.TIMING && (
          <div
            style={{
              width: Math.floor(width),
              height: roundedHeight - tabBarHeight,
              overflowY: 'auto',
              lineHeight: 0,
            }}
          >
            <iframe
              src={
                currentEvent.modules.timing.enabled
                  ? currentEvent.modules.timing.leaderboardUrl
                  : undefined
              }
              title="Event Leaderboard"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(function InfoSheet({
  maxHeight,
  isInitiallyOpen,
}: {
  maxHeight: number;
  isInitiallyOpen: boolean;
}) {
  return (
    <Sheet maxHeight={maxHeight} isInitiallyOpen={isInitiallyOpen}>
      {(width, height) => <InfoSheetContent width={width} height={height} />}
    </Sheet>
  );
});
